/**
 * @puffin-core/auth-client — the MFA ENROLMENT leg, shared by the platform and the tenant services.
 *
 * Two sign-ins answer "enrol a second factor first" instead of a session, in ONE shape: 403 `MFA_SETUP_REQUIRED`
 * carrying `details.mfaEnrollmentToken`, a ten-minute token that only the app's MFA setup routes accept
 *   - the platform login, for a staff member with no second factor (Q-L16-98 · F-98-16);
 *   - the tenant sign-in, for a person whose MFA an administrator's reset cleared (owner ruling Q-ARG-1,
 *     "Required again", 2026-10-08).
 * So the reading of that refusal, and the two calls that resolve it (`/mfa/setup/init` with the password, then
 * `/mfa/setup/verify` with the first code, each with the enrolment token as an explicit one-off Bearer), live here
 * once. The services differ only in the session they persist and the user key they project (`staff` vs `user` +
 * `locations`), which each injects.
 *
 * @module @puffin-core/auth-client/mfaEnrollment
 */

import {
  ENROLLMENT_FACTORS,
  FACTOR_ALREADY_HELD,
  FACTOR_SETUP_REQUIRED,
  FACTOR_SETUP_REQUIRED_STATUS,
  factorOfSetupRequired,
} from '@puffin-core/factor-enrollment-codes';
import { extractData, authError, networkError } from './internal.js';

/**
 * The sign-in refusal's code for an account that must enrol two-step verification before any session — the ONE
 * vocabulary's (`@puffin-core/factor-enrollment-codes`, SECREQ S6), re-exported for this package's callers.
 */
export const MFA_SETUP_REQUIRED_CODE = FACTOR_SETUP_REQUIRED.mfa;

/**
 * The `/mfa/setup/verify` refusal code for an account whose MFA is ALREADY enabled (`@puffin-core/auth-routes`, both
 * setup routers). It is the defined outcome of a completion replayed after its success response was lost: the
 * enrolment DID complete, so it is a state, not an error. The ONE vocabulary's, re-exported.
 */
export const MFA_ALREADY_ENABLED_CODE = FACTOR_ALREADY_HELD.mfa;

/**
 * The hold a sign-in refusal carries, or null when the refusal is anything else — the ONE reader of a factor hold
 * (SECREQ S6). A hold for two-step verification keeps its shape (`requiresMfaEnrollment`, the enrolment screen every
 * context already raises); a hold for a passkey or a PIN names its factor (`requiresFactorEnrollment`).
 *
 * @param {{status: number, body: *}} res
 * @returns {({requiresMfaEnrollment: true, mfaEnrollmentToken: string, expiresInSeconds: number}|
 *   {requiresFactorEnrollment: true, factor: string, mfaEnrollmentToken: string, expiresInSeconds: number}|null)}
 */
export function readEnrollmentRequired(res) {
  const error = res.body?.error;
  const factor = factorOfSetupRequired(error?.code);
  const token = error?.details?.mfaEnrollmentToken;
  if (res.status !== FACTOR_SETUP_REQUIRED_STATUS || !factor || typeof token !== 'string') {
    return null;
  }
  const hold = { mfaEnrollmentToken: token, expiresInSeconds: error.details.expiresInSeconds };
  return factor === 'mfa'
    ? { requiresMfaEnrollment: true, ...hold }
    : { requiresFactorEnrollment: true, factor, ...hold };
}

/**
 * Is this sign-in answer a HOLD (`readEnrollmentRequired`'s state) rather than a session (SECREQ S6b)? The ONE test of
 * that shape, for a host that runs a sign-in leg of its own (pos-web's raw passkey verify) and must raise the hold
 * instead of installing a session.
 *
 * @param {*} answer - a sign-in leg's answer
 * @returns {boolean}
 */
export function isSignInHold(answer) {
  return Boolean(answer?.requiresMfaEnrollment || answer?.requiresFactorEnrollment);
}

/**
 * The next factor's hold a completed enrolment answers instead of a session (SECREQ S5): the company policy, judged
 * again with the new factor held, still owes another. Read as a STATE, the shape a sign-in hold is read in, carrying
 * the `factor` to enrol next. An answer naming no token or an unknown factor cannot be followed, so it throws.
 *
 * @param {Object} enrollmentRequired - the completion's `enrollmentRequired`
 * @returns {{factor: string, mfaEnrollmentToken: string, expiresInSeconds: number}}
 */
function readOwedFactor(enrollmentRequired) {
  const { factor, mfaEnrollmentToken, expiresInSeconds } = enrollmentRequired;
  if (!ENROLLMENT_FACTORS.includes(factor) || typeof mfaEnrollmentToken !== 'string') {
    throw new TypeError(
      'An enrolment answered another factor owed in a shape the client cannot follow (SECREQ S5)',
    );
  }
  return { factor, mfaEnrollmentToken, expiresInSeconds };
}

/**
 * Build the begin / complete pair over one service's transport.
 *
 * @param {Object} deps
 * @param {Function} deps.request - the service's transport.
 * @param {string} deps.beginPath - `/mfa/setup/init` under the service's base.
 * @param {string} deps.completePath - `/mfa/setup/verify` under the service's base.
 * @param {Function} deps.persist - `(responseData) => void`: writes the minted session to the token store.
 * @param {Function} deps.project - `(responseData) => Object`: the user (and, for a tenant, locations) to return.
 * @param {string} deps.networkMessage - the service's network-failure wording.
 * @returns {{begin: Function, complete: Function}}
 */
export function buildMfaEnrollmentOperations({
  request,
  beginPath,
  completePath,
  persist,
  project,
  networkMessage,
}) {
  /**
   * Start the enrolment: the password is re-proved (one contract for every caller of `/mfa/setup/init`), and the
   * server answers the TOTP secret + otpauth URI.
   *
   * @param {string} mfaEnrollmentToken - from the sign-in refusal
   * @param {string} password
   * @returns {Promise<{secret: string, qrUri: string}>}
   */
  async function begin(mfaEnrollmentToken, password) {
    const res = await request({
      method: 'POST',
      path: beginPath,
      body: { password },
      auth: { token: mfaEnrollmentToken },
    });
    if (res.networkError) throw networkError(networkMessage);
    if (!res.ok) throw authError(res, 'MFA setup failed');
    const rd = extractData(res.body);
    return { secret: rd.secret, qrUri: rd.qrUri };
  }

  /**
   * Finish the enrolment with the first code: the server enables MFA and mints the session, which is persisted
   * exactly as a completed MFA challenge's is. A 409 `MFA_ALREADY_ENABLED` answers the `{ alreadyEnrolled: true }`
   * STATE instead of throwing (a lost success response); nothing is persisted and the person signs in again. A
   * completion whose company policy still owes ANOTHER factor (SECREQ S5) carries no session: it answers that factor's
   * hold beside the backup codes, and nothing is persisted. Every other refusal throws.
   *
   * @param {string} mfaEnrollmentToken
   * @param {string} code - the first TOTP code
   * @returns {Promise<(Object|{alreadyEnrolled: true}|{backupCodes: string[], enrollmentRequired: Object})>} the
   *   projected session + `backupCodes`, the state, or the next factor's hold.
   */
  async function complete(mfaEnrollmentToken, code) {
    const res = await request({
      method: 'POST',
      path: completePath,
      body: { code },
      auth: { token: mfaEnrollmentToken },
    });
    if (res.networkError) throw networkError(networkMessage);
    if (res.status === 409 && res.body?.error?.code === FACTOR_ALREADY_HELD.mfa) {
      return { alreadyEnrolled: true };
    }
    if (!res.ok) throw authError(res, 'MFA setup failed');
    const rd = extractData(res.body);
    if (rd.enrollmentRequired) {
      return {
        backupCodes: rd.backupCodes,
        enrollmentRequired: readOwedFactor(rd.enrollmentRequired),
      };
    }
    persist(rd);
    return { ...project(rd), backupCodes: rd.backupCodes };
  }

  return { begin, complete };
}

/**
 * Build the PASSKEY enrolment pair over one service's transport (SECREQ S6b): a sign-in the company MFA policy holds
 * for a passkey answers 403 `PASSKEY_SETUP_REQUIRED` with an enrolment token, and in an app that sets a passkey up the
 * passkey router's `/register/options` and `/register` accept that token (the tenant seam's `enrollment`,
 * `createFactorEnrollment`) as an explicit one-off Bearer — the stored access token does not exist yet. The router's
 * fresh-proof window is SHORTER than the token's life (the passkey seat's is 300 s), so a proof gone stale is refused
 * 401 `ENROLLMENT_PROOF_STALE`, and the person signs in again.
 *
 * @param {Object} deps
 * @param {Function} deps.request - the service's transport.
 * @param {string} deps.optionsPath - `/passkey/register/options` under the service's base.
 * @param {string} deps.registerPath - `/passkey/register` under the service's base.
 * @param {Function} deps.persist - `(responseData) => void`: writes the minted session to the token store.
 * @param {Function} deps.project - `(responseData) => Object`: the user (and locations) to return.
 * @param {string} deps.networkMessage - the service's network-failure wording.
 * @returns {{begin: Function, complete: Function}}
 */
export function buildPasskeyEnrollmentOperations({
  request,
  optionsPath,
  registerPath,
  persist,
  project,
  networkMessage,
}) {
  /**
   * Start the enrolment: the server answers the WebAuthn creation options, flat, as it answers a signed-in person's.
   *
   * @param {string} mfaEnrollmentToken - from the sign-in hold
   * @returns {Promise<Object>} the creation options
   */
  async function begin(mfaEnrollmentToken) {
    const res = await request({
      method: 'POST',
      path: optionsPath,
      body: {},
      auth: { token: mfaEnrollmentToken },
    });
    if (res.networkError) throw networkError(networkMessage);
    if (!res.ok) throw authError(res, 'Failed to get registration options');
    return extractData(res.body);
  }

  /**
   * Register the credential: the server keeps the passkey and mints the session, which is persisted exactly as a
   * password sign-in's is. The backup codes a passkey that turned two-step verification on earns (`mfa.backupCodes`)
   * are returned beside it, null when MFA was already on. A 409 `PASSKEY_ALREADY_ENROLLED` answers the
   * `{ alreadyEnrolled: true }` STATE (a lost success response); nothing is persisted and the person signs in again. A
   * completion whose company policy still owes ANOTHER factor carries no session: it answers that factor's hold beside
   * the backup codes, and nothing is persisted. Every other refusal throws.
   *
   * @param {string} mfaEnrollmentToken
   * @param {Object} credential - the formatted registration response
   * @param {string} [friendlyName=''] - the passkey's name; empty keeps the set-up's
   * @returns {Promise<(Object|{alreadyEnrolled: true}|{backupCodes: ?string[], enrollmentRequired: Object})>} the
   *   projected session + `backupCodes`, the state, or the next factor's hold.
   */
  async function complete(mfaEnrollmentToken, credential, friendlyName = '') {
    const res = await request({
      method: 'POST',
      path: registerPath,
      body: { credential, friendlyName },
      auth: { token: mfaEnrollmentToken },
    });
    if (res.networkError) throw networkError(networkMessage);
    if (res.status === 409 && res.body?.error?.code === FACTOR_ALREADY_HELD.passkey) {
      return { alreadyEnrolled: true };
    }
    if (!res.ok) throw authError(res, 'Passkey registration failed');
    const rd = extractData(res.body);
    const backupCodes = rd.mfa?.backupCodes ?? null;
    if (rd.enrollmentRequired) {
      return { backupCodes, enrollmentRequired: readOwedFactor(rd.enrollmentRequired) };
    }
    persist(rd);
    return { ...project(rd), backupCodes };
  }

  return { begin, complete };
}

/**
 * Build the PIN enrolment completion over one service's transport (SECREQ S6c (c)): a sign-in the company MFA policy
 * holds for a PIN answers 403 `PIN_SETUP_REQUIRED` with an enrolment token, and on pos — the one app whose seat holds
 * a person for a PIN — `/device-pin/register` accepts that token (the PIN's arm of `createFactorEnrollment`) as an
 * explicit one-off Bearer, the stored access token not existing yet. One leg: a PIN has no ceremony to begin. Setting a
 * PIN is never a device registration, so the server sets it only on a register someone already set up (409
 * `PIN_TERMINAL_NOT_REGISTERED`); a PIN another person in the company holds is refused 409 `PIN_IN_USE`. Each refusal
 * keeps its code for the step to word.
 *
 * @param {Object} deps
 * @param {Function} deps.request - the service's transport.
 * @param {string} deps.registerPath - `/device-pin/register` under the service's base.
 * @param {Function} deps.persist - `(responseData) => void`: writes the minted session to the token store.
 * @param {Function} deps.project - `(responseData) => Object`: the user (and locations) to return.
 * @param {string} deps.networkMessage - the service's network-failure wording.
 * @returns {{complete: Function}}
 */
export function buildPinEnrollmentOperations({
  request,
  registerPath,
  persist,
  project,
  networkMessage,
}) {
  /**
   * Set the PIN: the server keeps it for this register and mints the session, which is persisted exactly as a password
   * sign-in's is. A completion whose company policy still owes ANOTHER factor carries no session: it answers that
   * factor's hold, and nothing is persisted. Every refusal throws.
   *
   * @param {string} mfaEnrollmentToken - from the sign-in hold
   * @param {{pin: string, deviceFingerprint: Object, deviceToken: string}} pinSet - the PIN and THIS register's own
   *   fingerprint and device token; nothing here invents either.
   * @returns {Promise<(Object|{enrollmentRequired: Object})>} the projected session, or the next factor's hold.
   */
  async function complete(mfaEnrollmentToken, pinSet) {
    const res = await request({
      method: 'POST',
      path: registerPath,
      body: { ...pinSet },
      auth: { token: mfaEnrollmentToken },
    });
    if (res.networkError) throw networkError(networkMessage);
    if (!res.ok) throw authError(res, 'PIN setup failed');
    const rd = extractData(res.body);
    if (rd.enrollmentRequired) {
      return { enrollmentRequired: readOwedFactor(rd.enrollmentRequired) };
    }
    persist(rd);
    return project(rd);
  }

  return { complete };
}
